import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  LineSourceDto,
  QueuedIngest,
  type SourceMetadataInput,
  ComputeResultDto,
  LineItemDto,
  ListResponse,
  PricingDetailDto,
  PricingDto,
  PricingFiltersInput,
  ProjectDto,
} from "@reina/shared";
import type {
  LineOverrideInput,
  PricingInput,
  PricingStatus,
  ProjectInput,
} from "@reina/shared";
import { api } from "./client";
import { uploadViaPresign } from "../lib/uploads";

/**
 * Projects + pricings hooks (contract §Projects & pricings). Buy-list ingest is
 * async on the server (202 `{staged}` → status matching → ready), so the
 * pricing-detail query polls every 2s while status is "matching".
 */

const ProjectList = ListResponse(ProjectDto);
const PricingList = ListResponse(PricingDto);

const POLL_MS = 2000;

const pricingKey = (id: string) => ["pricings", "detail", id] as const;

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export type ProjectListParams = {
  status?: string;
  limit?: number;
  offset?: number;
};

export function useProjects(params: ProjectListParams = { limit: 500 }) {
  return useQuery({
    queryKey: ["projects", "list", params],
    queryFn: () => api.get("/projects", { query: params, schema: ProjectList }),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ["projects", "detail", id],
    queryFn: () => api.get(`/projects/${id}`, { schema: ProjectDto }),
    // Callers may pass "" while a parent query resolves (e.g. pricing → project).
    enabled: id.length > 0,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectInput) => api.post("/projects", input, { schema: ProjectDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ProjectInput> }) =>
      api.patch(`/projects/${id}`, input, { schema: ProjectDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });
}

// ---------------------------------------------------------------------------
// Pricings nested under a project
// ---------------------------------------------------------------------------

export function useProjectPricings(projectId: string) {
  return useQuery({
    queryKey: ["projects", "pricings", projectId],
    queryFn: () =>
      api.get(`/projects/${projectId}/pricings`, { query: { limit: 500 }, schema: PricingList }),
  });
}

export function useCreatePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: string; input: PricingInput }) =>
      api.post(`/projects/${projectId}/pricings`, input, { schema: PricingDto }),
    onSuccess: (_data, { projectId }) =>
      queryClient.invalidateQueries({ queryKey: ["projects", "pricings", projectId] }),
  });
}

// ---------------------------------------------------------------------------
// Pricing detail + lines + filters + compute
// ---------------------------------------------------------------------------

export function usePricing(id: string) {
  return useQuery({
    queryKey: pricingKey(id),
    queryFn: () => api.get(`/pricings/${id}`, { schema: PricingDetailDto }),
    refetchInterval: (query) => (query.state.data?.status === "matching" ? POLL_MS : false),
  });
}

export type PricingPatch = Partial<PricingInput> & { status?: PricingStatus };

export function useUpdatePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PricingPatch }) =>
      api.patch(`/pricings/${id}`, input, { schema: PricingDto }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: pricingKey(data.id) });
      void queryClient.invalidateQueries({ queryKey: ["projects", "pricings", data.projectId] });
    },
  });
}

export function useSaveFilters() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: z.input<typeof PricingFiltersInput> }) =>
      api.put(`/pricings/${id}/filters`, input, { schema: PricingFiltersInput }),
    onSuccess: (_data, { id }) => queryClient.invalidateQueries({ queryKey: pricingKey(id) }),
  });
}

/** `POST /api/pricings/:id/lines*` responds `202 {sourceId, queued}` — the
 * worker stages + matches in the background; the detail query polls. */
export function usePasteBuyList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      api.post(`/pricings/${id}/lines`, { text }, { schema: QueuedIngest }),
    onSuccess: (_data, { id }) => queryClient.invalidateQueries({ queryKey: pricingKey(id) }),
  });
}

export function useUploadBuyList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const { uploadKey, filename } = await uploadViaPresign(file, "buy_list");
      return api.post(
        `/pricings/${id}/lines/from-key`,
        { uploadKey, filename },
        { schema: QueuedIngest },
      );
    },
    onSuccess: (_data, { id }) => queryClient.invalidateQueries({ queryKey: pricingKey(id) }),
  });
}

export function useOverrideLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pricingId,
      lineId,
      input,
    }: {
      pricingId: string;
      lineId: string;
      input: LineOverrideInput;
    }) => api.patch(`/pricings/${pricingId}/lines/${lineId}`, input, { schema: LineItemDto }),
    onSuccess: (_data, { pricingId }) =>
      queryClient.invalidateQueries({ queryKey: pricingKey(pricingId) }),
  });
}

/** `DELETE /api/pricings/:id` — removes the pricing, its lines and results. */
export function useDeletePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; projectId: string }) => api.del(`/pricings/${id}`),
    onSuccess: (_data, { id, projectId }) => {
      queryClient.removeQueries({ queryKey: pricingKey(id) });
      void queryClient.invalidateQueries({ queryKey: ["projects", "pricings", projectId] });
    },
  });
}

/** `POST /api/pricings/:id/lines/add` — append one line, matched synchronously. */
export function useAddLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pricingId, text }: { pricingId: string; text: string }) =>
      api.post(`/pricings/${pricingId}/lines/add`, { text }, { schema: LineItemDto }),
    onSuccess: (_data, { pricingId }) =>
      queryClient.invalidateQueries({ queryKey: pricingKey(pricingId) }),
  });
}

/** `DELETE /api/pricings/:id/lines/:lineId`. */
export function useDeleteLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pricingId, lineId }: { pricingId: string; lineId: string }) =>
      api.del(`/pricings/${pricingId}/lines/${lineId}`),
    onSuccess: (_data, { pricingId }) =>
      queryClient.invalidateQueries({ queryKey: pricingKey(pricingId) }),
  });
}

/** `POST /api/pricings/:id/lines/delete` — bulk removal of selected lines. */
const DeletedResult = z.object({ deleted: z.number().int() });
export function useDeleteLines() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pricingId, lineIds }: { pricingId: string; lineIds: string[] }) =>
      api.post(`/pricings/${pricingId}/lines/delete`, { lineIds }, { schema: DeletedResult }),
    onSuccess: (_data, { pricingId }) =>
      queryClient.invalidateQueries({ queryKey: pricingKey(pricingId) }),
  });
}

/** `PATCH /api/pricings/:id/sources/:sourceId` — edit source metadata. */
export function useUpdateSourceMeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pricingId,
      sourceId,
      input,
    }: {
      pricingId: string;
      sourceId: string;
      input: SourceMetadataInput;
    }) => api.patch(`/pricings/${pricingId}/sources/${sourceId}`, input, { schema: LineSourceDto }),
    onSuccess: (_data, { pricingId }) =>
      queryClient.invalidateQueries({ queryKey: pricingKey(pricingId) }),
  });
}

/** URL of a source's stored original (same-origin; session cookie applies). */
export function sourceFileUrl(pricingId: string, sourceId: string): string {
  return `/api/pricings/${pricingId}/sources/${sourceId}/file`;
}

export function useComputePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/pricings/${id}/compute`, undefined, { schema: ComputeResultDto }),
    onSuccess: (_data, id) => queryClient.invalidateQueries({ queryKey: pricingKey(id) }),
  });
}
